import { supabase } from './supabase';

interface BluetoothDevice {
  id: string;
  name: string;
  type: 'samsung_fit' | 'google_fit' | 'other';
  connected: boolean;
  lastSync?: Date;
}

export async function scanForDevices(): Promise<BluetoothDevice[]> {
  if (!navigator.bluetooth) {
    throw new Error('Bluetooth non disponible sur cet appareil');
  }

  try {
    const device = await navigator.bluetooth.requestDevice({
      filters: [
        {
          services: ['heart_rate'],
          name: 'Galaxy Fit3'
        }
      ],
      optionalServices: [
        'battery_service',
        'device_information',
        'health_thermometer'
      ]
    });

    if (device) {
      return [{
        id: device.id,
        name: device.name || 'Appareil inconnu',
        type: device.name?.includes('Galaxy') ? 'samsung_fit' : 'other',
        connected: device.gatt?.connected || false
      }];
    }

    return [];
  } catch (error) {
    console.error('Erreur lors du scan Bluetooth:', error);
    throw error;
  }
}

export async function connectToDevice(deviceId: string): Promise<boolean> {
  try {
    const device = await navigator.bluetooth.requestDevice({
      filters: [{ services: ['heart_rate'] }]
    });

    const server = await device.gatt?.connect();
    if (!server) {
      throw new Error('Impossible de se connecter au serveur GATT');
    }

    // Sauvegarder l'état de connexion
    await supabase
      .from('watch_settings')
      .upsert({
        device_id: deviceId,
        connected: true,
        last_sync: new Date().toISOString()
      });

    return true;
  } catch (error) {
    console.error('Erreur de connexion Bluetooth:', error);
    throw error;
  }
}

export async function disconnectDevice(deviceId: string): Promise<void> {
  try {
    // Mettre à jour l'état de connexion
    await supabase
      .from('watch_settings')
      .update({
        connected: false,
        last_sync: new Date().toISOString()
      })
      .eq('device_id', deviceId);
  } catch (error) {
    console.error('Erreur de déconnexion:', error);
    throw error;
  }
}

export async function startHeartRateMonitoring(onHeartRate: (value: number) => void) {
  try {
    const device = await navigator.bluetooth.requestDevice({
      filters: [{ services: ['heart_rate'] }]
    });

    const server = await device.gatt?.connect();
    const service = await server?.getPrimaryService('heart_rate');
    const characteristic = await service?.getCharacteristic('heart_rate_measurement');

    await characteristic?.startNotifications();
    characteristic?.addEventListener('characteristicvaluechanged', (event: any) => {
      const value = event.target.value;
      const heartRate = value.getUint8(1);
      onHeartRate(heartRate);
    });

    return true;
  } catch (error) {
    console.error('Erreur lors du monitoring cardiaque:', error);
    throw error;
  }
}

export async function getDeviceBatteryLevel(deviceId: string): Promise<number> {
  try {
    const device = await navigator.bluetooth.requestDevice({
      filters: [{ services: ['battery_service'] }]
    });

    const server = await device.gatt?.connect();
    const service = await server?.getPrimaryService('battery_service');
    const characteristic = await service?.getCharacteristic('battery_level');
    
    const value = await characteristic?.readValue();
    return value ? value.getUint8(0) : 0;
  } catch (error) {
    console.error('Erreur lors de la lecture du niveau de batterie:', error);
    throw error;
  }
}